import {throwError as observableThrowError,  Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { tap, catchError } from 'rxjs/operators';
import {JsonService} from "../../../services/core/json.service";
import {VendorApiResources} from "./vendor.constants";

@Injectable({
  providedIn: 'root'
})
export class VendorDataService {
  private baseURL;

  constructor(
    private http: HttpClient,
    private jsonService: JsonService,
  ) {
    this.baseURL = VendorApiResources;
  }

  list(model: any = null): Observable<any> {
    const endPoint = `${this.baseURL.list}`;
    return this.http.post<any>(endPoint, model)
                    .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  getList(requestparam:any = {}): Observable<any> {
    let params = new HttpParams();
    for (const key in requestparam) {
      if (requestparam.hasOwnProperty(key)) {
        params = params.append(key, requestparam[key]);
      }
    }

    const endPoint = `${this.baseURL.list}`;
    return this.http.get<any>(endPoint+'/list', {params})
                    .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }


  search(model:any): Observable<any> {
    const endPoint = `${this.baseURL.list}`;
    return this.http.post<any>(endPoint, model)
                    .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  create(model: any): Observable<any> {
    const endPoint = `${this.baseURL.create}`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  detail(id: any, params?: any): Observable<any> {
    const endPoint = `${this.baseURL.detail}/${id}`;
    return this.http.get<any>(endPoint, { params }) 
      .pipe(
        tap(data => this.jsonService.doStringify(data)), 
        catchError(this.errorHandlerMessage)
      );
  } 

  getByName(name: any): Observable<any> {
    const endPoint = `${this.baseURL.getByName}/${name}`;
    return this.http.get<any>(endPoint) 
      .pipe(
        tap(data => this.jsonService.doStringify(data)), 
        catchError(this.errorHandlerMessage)
      );
  }

  update(id:any, model: any): Observable<any> {
    const endPoint = `${this.baseURL.update}/${id}`;
    return this.http.patch<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  delete(id:any): Observable<any> {
    const endPoint = `${this.baseURL.delete}/${id}`;
    return this.http.delete<any>(endPoint)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  // Bulk delete: DELETE vendors/delete/multiple with { vendor_ids: [...] }
  deleteMultiple(vendorIds: any[]): Observable<any> {
    const endPoint = `${this.baseURL.deleteMultiple}`;
    return this.http.request<any>('delete', endPoint, { body: { vendor_ids: vendorIds } })
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  // Pre-check which of the selected vendors can be deleted (and why not, for the rest)
  deleteAvailability(vendorIds: any[]): Observable<any> {
    const endPoint = `${this.baseURL.deleteAvailability}`;
    return this.http.post<any>(endPoint, { vendor_ids: vendorIds })
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  onChangeStatus(id:any, model: any): Observable<any> {
    const endPoint = `${this.baseURL.changeStatus}/${id}`;
    return this.http.patch<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  copyVendor(id: any, model: any): Observable<any> {
    const endPoint = `${this.baseURL.copy}/${id}`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  // Sub Companies (child vendors)

  getSubCompanies(id: any, requestparam: any = {}): Observable<any> {
    const endPoint = `${this.baseURL.subCompanies}/${id}/sub-companies`;
    return this.http.get<any>(endPoint, { params: this.toHttpParams(requestparam) })
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  // Picker rows. Vendors that can't be attached come back with
  // is_available=false and a reason instead of being filtered out.
  getSubCompanyCandidates(id: any, requestparam: any = {}): Observable<any> {
    const endPoint = `${this.baseURL.subCompanies}/${id}/sub-companies/candidates`;
    return this.http.get<any>(endPoint, { params: this.toHttpParams(requestparam) })
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  addSubCompanies(id: any, vendorIds: any[]): Observable<any> {
    const endPoint = `${this.baseURL.subCompanies}/${id}/sub-companies`;
    return this.http.post<any>(endPoint, { vendor_ids: vendorIds })
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  removeSubCompany(id: any, subCompanyId: any): Observable<any> {
    const endPoint = `${this.baseURL.subCompanies}/${id}/sub-companies/${subCompanyId}`;
    return this.http.delete<any>(endPoint)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  private toHttpParams(requestparam: any = {}): HttpParams {
    let params = new HttpParams();
    Object.keys(requestparam || {}).forEach(key => {
      if (requestparam[key] !== null && requestparam[key] !== undefined && requestparam[key] !== '') {
        params = params.append(key, requestparam[key]);
      }
    });
    return params;
  }

  // Import Methods
  downloadSampleFile(): Observable<any> {
    const endPoint = `${this.baseURL.downloadSample}`;
    return this.http.get(endPoint, { responseType: 'blob' })
      .pipe(catchError(this.errorHandlerMessage));
  }

  uploadImportFile(file: File): Observable<any> {
    const endPoint = `${this.baseURL.uploadImport}`;
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<any>(endPoint, formData)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  validateImportData(data: any): Observable<any> {
    const endPoint = `${this.baseURL.validateImport}`;
    return this.http.post<any>(endPoint, data)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  executeImport(data: any): Observable<any> {
    const endPoint = `${this.baseURL.executeImport}`;
    return this.http.post<any>(endPoint, data)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  getImportHistory(params: any = {}): Observable<any> {
    const endPoint = `${this.baseURL.importHistory}`;
    let httpParams = new HttpParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined) {
        httpParams = httpParams.append(key, params[key]);
      }
    });
    
    return this.http.get<any>(endPoint, { params: httpParams })
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  getImportHistoryDetails(id: number): Observable<any> {
    const endPoint = `${this.baseURL.importHistory}/${id}`;
    return this.http.get<any>(endPoint)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  deleteImportHistory(id: number): Observable<any> {
    const endPoint = `${this.baseURL.importHistory}/${id}`;
    return this.http.delete<any>(endPoint)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  errorHandler(error: HttpErrorResponse) {
    return observableThrowError(error.error || 'Server Error');
  }

  errorHandlerMessage(error: HttpErrorResponse) {
    return observableThrowError(error.error || 'Server Error');
  }
}
